import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import {
  ArrowLeft,
  RefreshCcw,
  Wallet,
  Calendar,
  Clock,
  TrendingUp,
  TrendingDown,
  ArrowRightLeft,
  Search,
  FileText,
  AlertCircle,
  CheckCircle2,
  Banknote,
  CreditCard,
  QrCode,
  Activity,
  ChevronUp,
  ChevronDown,
  Check,
  ShieldCheck,
  Undo2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useCashCloseAccess } from '../hooks/useCashCloseAccess';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { canReviewArqueos, isArqueoControlable, resumenControl } from '../utils/arqueoReview';
import { formatOperationDateTime } from '../utils/localDateTime';
import ControlArqueoModal from './caja/ControlArqueoModal';

function CajaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Esta pantalla ES el cierre completo: teorico, real y diferencia por metodo.
  // Con cierre ciego activo solo entra quien tenga `caja-cierre-completo`.
  const { canViewFullClose, resolvingCashCloseAccess, isSuperAdmin } = useCashCloseAccess();
  const { profile, organization } = useAuth();
  const { sites } = useSite();
  const location = useLocation();

  // Pestaña de Caja desde la que se abrió el detalle; "Volver" la reabre.
  const origen = location.state?.from === 'control' || location.state?.from === 'historial'
    ? location.state.from
    : null;
  const volver = () => navigate('/sistema/caja', origen ? { state: { tab: origen } } : undefined);

  // Control de arqueos: misma regla que la pestaña (lógico activo + admin).
  const puedeControlarArqueos = canReviewArqueos({ organization, profile, isSuperAdmin });
  const [controlModal, setControlModal] = useState(null); // 'marcar' | 'deshacer'

  const [loading, setLoading] = useState(true);
  const [turno, setTurno] = useState(null);
  const [q, setQ] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'nombre', direction: 'asc' });

  useEffect(() => {
    if (resolvingCashCloseAccess) return;
    if (!canViewFullClose) {
      toast.error('No tenés permiso para ver el detalle del cierre.', { id: 'caja-detalle-denied' });
      navigate('/sistema/caja', { replace: true });
      return;
    }
    if (id) fetchDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canViewFullClose, resolvingCashCloseAccess]);

  async function fetchDetalle() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('turnos_caja')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setTurno(data);
    } catch (e) {
      console.error(e);
      setTurno(null);
    } finally {
      setLoading(false);
    }
  }

  const money = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });

  const resumenFiltrado = useMemo(() => {
    const needle = (q || '').trim().toLowerCase();
    const arr = Array.isArray(turno?.resumen_cierre) ? turno.resumen_cierre : [];
    if (!needle) return arr;
    return arr.filter((r) => String(r?.nombre || '').toLowerCase().includes(needle));
  }, [turno?.resumen_cierre, q]);

  const sortedResumen = useMemo(() => {
    const items = [...resumenFiltrado];
    const { key, direction } = sortConfig;

    return items.sort((a, b) => {
      const valueMap = {
        nombre: [a?.nombre || '', b?.nombre || ''],
        teorico: [Number(a?.teorico || 0), Number(b?.teorico || 0)],
        real: [Number(a?.real || 0), Number(b?.real || 0)],
        diferencia: [Number(a?.diferencia || 0), Number(b?.diferencia || 0)],
      };

      const [valueA, valueB] = valueMap[key] || [a?.nombre || '', b?.nombre || ''];
      if (typeof valueA === 'number' || typeof valueB === 'number') {
        return direction === 'asc' ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }
      const safeA = String(valueA).toLowerCase();
      const safeB = String(valueB).toLowerCase();
      if (safeA < safeB) return direction === 'asc' ? -1 : 1;
      if (safeA > safeB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [resumenFiltrado, sortConfig]);

  const kpis = useMemo(() => {
    const apertura = turno?.fecha_apertura ? new Date(turno.fecha_apertura) : null;
    const cierre = turno?.fecha_cierre ? new Date(turno.fecha_cierre) : null;
    const saldoInicial = Number(turno?.saldo_inicial || 0);
    const totalReal = Number(turno?.total_real || 0);
    const diferencia = Number(turno?.diferencia || 0);
    const ingresos = Number(turno?.total_ingresos || 0);
    const egresos = Number(turno?.total_egresos || 0);
    const gastos = Number(turno?.total_gastos || 0);
    return { apertura, cierre, saldoInicial, totalReal, diferencia, ingresos, egresos, gastos };
  }, [turno]);

  const getMethodIcon = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('efectivo')) return Banknote;
    if (n.includes('tarjeta') || n.includes('debito') || n.includes('credito')) return CreditCard;
    if (n.includes('qr') || n.includes('mercado')) return QrCode;
    if (n.includes('transf')) return ArrowRightLeft;
    return Wallet;
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const FilterHint = () => <div className="h-2.5 w-2.5 opacity-30" />;

  const SortableTh = ({ label, sortKey, align = 'left' }) => {
    const isActive = sortConfig.key === sortKey;

    return (
      <th
        className={`px-3.5 py-3.5 cursor-pointer hover:bg-slate-100 transition-colors select-none ${
          align === 'right' ? 'text-right' : 'text-left'
        }`}
        onClick={() => handleSort(sortKey)}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
          <span>{label}</span>
          <span className="flex flex-col">
            {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
            {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
            {!isActive && <FilterHint />}
          </span>
        </div>
      </th>
    );
  };

  // Mientras se resuelve el permiso (o si no lo tiene y el efecto todavía no
  // redirigió) no se pinta nada del arqueo.
  if (loading || resolvingCashCloseAccess || !canViewFullClose) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD]" size={40} />
      </div>
    );
  }

  if (!turno) {
    return (
      <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
                <Wallet size={18} className="text-[#3FF2AA]" />
              </div>
              <div>
                <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight leading-none">Caja</h1>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">
                  Detalle de arqueo
                </p>
              </div>
            </div>

            <button
              onClick={volver}
              className="px-6 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
              <ArrowLeft size={16} strokeWidth={3} /> Volver
            </button>
          </div>

          <div className="py-20 text-center opacity-40 italic text-[10px] font-black uppercase tracking-widest">
            No se encontró el registro del turno.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER NUEVO (Estilo Productos) */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Wallet size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 tracking-tight uppercase">Caja · Detalle</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Arqueo #{String(turno?.id || '').split('-')[0]?.toUpperCase()} · Operador: {turno?.usuario_email || '—'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center w-full lg:w-auto">
            <button
                onClick={volver}
                className="flex-1 sm:flex-none justify-center px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
                <ArrowLeft size={16} /> Volver
            </button>
            <button
                onClick={fetchDetalle}
                className="flex-1 sm:flex-none justify-center px-4 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            >
                <RefreshCcw size={16} /> Refrescar
            </button>
        </div>
      </div>

      {/* CONTROL DEL ARQUEO */}
      {puedeControlarArqueos && (() => {
        const control = resumenControl(turno);
        const controlado = control.estado === 'controlado';
        const controlable = isArqueoControlable({ turno, organization });
        if (!controlado && !controlable) return null;
        return (
          <div
            className={`mb-3 flex flex-col gap-3 rounded-2xl border p-3.5 shadow-sm sm:flex-row sm:items-center ${
              controlado ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
            }`}
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <ShieldCheck size={18} className={controlado ? 'text-emerald-600' : 'text-amber-600'} />
              <div className="min-w-0">
                <p className={`text-[10px] font-black uppercase tracking-widest ${controlado ? 'text-emerald-700' : 'text-amber-800'}`}>
                  {controlado ? 'Arqueo controlado' : 'Pendiente de control'}
                </p>
                <p className="mt-0.5 text-[9px] font-bold uppercase tracking-widest text-slate-500">
                  {controlado
                    ? `Por ${control.email || 'usuario borrado'} · ${formatOperationDateTime(control.at)}${control.autocontrol ? ' · lo controló quien cerró el turno' : ''}`
                    : 'Revisá el detalle por método y marcalo cuando esté conforme.'}
                </p>
                {controlado && control.observacion && (
                  <p className="mt-1 text-[10px] font-bold text-slate-600">“{control.observacion}”</p>
                )}
              </div>
            </div>
            {controlado ? (
              <button
                type="button"
                onClick={() => setControlModal('deshacer')}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-500 transition-all hover:text-[#0B2A36]"
              >
                <Undo2 size={12} /> Deshacer
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setControlModal('marcar')}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#0B2A36] px-4 py-2.5 text-[9px] font-black uppercase tracking-widest text-white shadow-sm transition-all hover:brightness-110"
              >
                <Check size={12} /> Marcar controlado
              </button>
            )}
          </div>
        );
      })()}

      {controlModal && (
        <ControlArqueoModal
          turno={turno}
          accion={controlModal}
          profile={profile}
          sitioNombre={
            turno?.sitio_id
              ? sites.find((st) => String(st.id) === String(turno.sitio_id))?.nombre || ''
              : ''
          }
          onClose={() => setControlModal(null)}
          onDone={async ({ aplicado, accion }) => {
            setControlModal(null);
            // Desde el tablero, aprobar devuelve a la lista para seguir con el
            // próximo pendiente. En cualquier otro caso se queda y refresca.
            if (aplicado && accion === 'marcar' && origen === 'control') {
              volver();
              return;
            }
            await fetchDetalle();
          }}
        />
      )}

      {/* HEADER ANTERIOR (COMENTADO)
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 shadow-sm">
            <Wallet size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800 uppercase tracking-tight leading-none">Caja · Detalle</h1>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">
              Arqueo #{String(turno?.id || '').split('-')[0]?.toUpperCase()} · Operador: {turno?.usuario_email || '—'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
           ... (botones viejos)
        </div>
      </div>
      */}

      {/* KPIs NUEVOS (Estilo Productos) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5 mb-3">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Apertura</p>
            <Clock size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg sm:text-xl font-black text-slate-800 tracking-tight">
            {kpis.apertura
                ? kpis.apertura.toLocaleString('es-AR', {
                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                  })
                : '—'}
          </p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cierre</p>
            <Calendar size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg sm:text-xl font-black text-slate-800 tracking-tight">
             {kpis.cierre
                ? kpis.cierre.toLocaleString('es-AR', {
                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                  })
                : '—'}
          </p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Real</p>
            <TrendingUp size={16} className="text-emerald-300" />
          </div>
          <p className="mt-1 text-xl sm:text-2xl font-black text-slate-800 tracking-tight">$ {money(kpis.totalReal)}</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Diferencia</p>
             {kpis.diferencia === 0 ? <CheckCircle2 size={16} className="text-emerald-500" /> : <AlertCircle size={16} className="text-rose-500" />}
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${
                 kpis.diferencia < 0
                  ? 'text-rose-600'
                  : kpis.diferencia > 0
                  ? 'text-emerald-600'
                  : 'text-slate-800'
              }`}>
              {kpis.diferencia === 0 ? 'OK' : `$ ${money(kpis.diferencia)}`}
          </p>
        </div>
      </div>

      {/* KPIs ANTERIORES (COMENTADO)
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
           ...
        </div>
        ...
      </div>
      */}

      {/* BARRA DE BÚSQUEDA NUEVA (Estilo Productos) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-3">
         <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-2 items-center">
            <div className="sm:col-span-2 xl:col-span-8 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="BUSCAR MÉTODO (EFECTIVO, TARJETA...)"
                className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                />
            </div>
            <div className="xl:col-span-4 flex gap-2">
                <div className="flex-1 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none">Saldo inicial</p>
                    <p className="text-[12px] font-black text-slate-800 tracking-tight mt-0.5">$ {money(kpis.saldoInicial)}</p>
                </div>
                <div className="flex-1 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none">Estado</p>
                    <p className="text-[12px] font-black text-slate-800 tracking-tight mt-0.5 uppercase">{turno?.estado || '—'}</p>
                </div>
            </div>
         </div>
      </div>

      {/* LISTADO (TABLA) NUEVO ESTILO */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-4">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
           <div className="flex items-center gap-2">
            <Activity size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Desglose por método
            </span>
          </div>
          <span className="text-[9px] font-bold uppercase tracking-widest text-white/60 hidden sm:inline-block">
             Teórico vs Real
          </span>
        </div>

        {/* TABLA ANTERIOR HEADER (COMENTADO)
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
             ...
          </div>
        </div>
        */}

        <div className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
          {sortedResumen.map((r, idx) => {
            const Icon = getMethodIcon(r?.nombre);
            const teorico = Number(r?.teorico || 0);
            const real = Number(r?.real || 0);
            const dif = Number(r?.diferencia || 0);

            return (
              <div key={`${r?.nombre || 'm'}-${idx}-mobile`} className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-xl bg-slate-50 text-slate-500 border border-slate-100">
                      <Icon size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-black text-slate-800 uppercase truncate">{r?.nombre || '-'}</p>
                      <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{dif === 0 ? 'Sin desvios' : dif < 0 ? 'Faltante' : 'Sobrante'}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-black ${dif < 0 ? 'text-rose-600' : dif > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {dif === 0 ? 'OK' : `$ ${money(dif)}`}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Teorico</p>
                    <p className="mt-1 text-[10px] font-black text-slate-700">$ {money(teorico)}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Real</p>
                    <p className="mt-1 text-[10px] font-black text-slate-700">$ {money(real)}</p>
                  </div>
                </div>
              </div>
            );
          })}
          {sortedResumen.length === 0 && (
            <div className="py-16 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
              Sin resultados para mostrar
            </div>
          )}
        </div>

        <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-left min-w-[720px]">
          <thead>
            <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
              <SortableTh label="Metodo" sortKey="nombre" />
              <SortableTh label="Teorico" sortKey="teorico" align="right" />
              <SortableTh label="Real" sortKey="real" align="right" />
              <SortableTh label="Diferencia" sortKey="diferencia" align="right" />
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-50">
            {sortedResumen.map((r, idx) => {
              const Icon = getMethodIcon(r?.nombre);
              const teorico = Number(r?.teorico || 0);
              const real = Number(r?.real || 0);
              const dif = Number(r?.diferencia || 0);

              return (
                <tr key={`${r?.nombre || 'm'}-${idx}`} className="hover:bg-blue-50/20 transition-all">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-slate-50 text-slate-500 border border-slate-100">
                        <Icon size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-black text-slate-800 uppercase tracking-tight truncate">
                          {r?.nombre || '—'}
                        </p>
                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest italic">
                          {dif === 0 ? 'Sin desvíos' : dif < 0 ? 'Faltante' : 'Sobrante'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-[11px] font-black text-slate-700 tracking-tight">
                    $ {money(teorico)}
                  </td>
                  <td className="px-6 py-4 text-right text-[11px] font-black text-slate-800 tracking-tight">
                    $ {money(real)}
                  </td>
                  <td
                    className={`px-6 py-4 text-right text-[11px] font-black tracking-tight ${
                      dif < 0 ? 'text-rose-600' : dif > 0 ? 'text-emerald-600' : 'text-slate-400'
                    }`}
                  >
                    {dif === 0 ? '—' : `$ ${money(dif)}`}
                  </td>
                </tr>
              );
            })}

            {sortedResumen.length === 0 && (
              <tr>
                <td colSpan={4} className="py-20 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
                  Sin resultados para mostrar
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

      {/* OBSERVACIONES (Estilo mantenido pero con bordes redondeados consistentes) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-10">
        <div className="lg:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-xl bg-slate-50 text-slate-600 border border-slate-100">
              <FileText size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-800 uppercase tracking-tight">Observaciones</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 italic">
                Notas del operador al cierre
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
            <p className="text-[11px] font-bold text-slate-700 italic leading-relaxed uppercase">
              {turno?.observaciones_cierre ? `“${turno.observaciones_cierre}”` : '— Sin observaciones —'}
            </p>
          </div>
        </div>

        <div className="lg:col-span-4 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
              <TrendingDown size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-800 uppercase tracking-tight">Resumen ejecutivo</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 italic">
                Para auditoría rápida
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between bg-emerald-50/60 border border-emerald-100 rounded-xl px-4 py-3">
              <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">Ingresos</span>
              <span className="text-[11px] font-black text-emerald-700">+ $ {money(kpis.ingresos)}</span>
            </div>
            <div className="flex items-center justify-between bg-rose-50/60 border border-rose-100 rounded-xl px-4 py-3">
              <span className="text-[9px] font-black text-rose-600 uppercase tracking-widest">Egresos / Pagos</span>
              <span className="text-[11px] font-black text-rose-700">- $ {money(kpis.egresos)}</span>
            </div>
            <div className="flex items-center justify-between bg-rose-50/60 border border-rose-100 rounded-xl px-4 py-3">
              <span className="text-[9px] font-black text-rose-600 uppercase tracking-widest">Gastos</span>
              <span className="text-[11px] font-black text-rose-700">- $ {money(kpis.gastos)}</span>
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Total real</span>
              <span className="text-[11px] font-black text-slate-800">$ {money(kpis.totalReal)}</span>
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Diferencia</span>
              <span
                className={`text-[11px] font-black ${
                  kpis.diferencia < 0 ? 'text-rose-600' : kpis.diferencia > 0 ? 'text-emerald-600' : 'text-slate-700'
                }`}
              >
                {kpis.diferencia === 0 ? 'OK' : `$ ${money(kpis.diferencia)}`}
              </span>
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Estado</span>
              <span className="text-[11px] font-black text-slate-800 uppercase">{turno?.estado || '—'}</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }
      `}</style>
    </div>
  );
}

export default CajaDetalle;


